import { Box, Stack } from '@mui/material';
import React, { useMemo } from 'react';

import { DynamicCard, DynamicCardAccessory, DynamicCardContent, DynamicCardMedia, SizingMultiplier } from '@ocx/ui';
import { BannerActionType } from '@ocx/graphql';

import { ExternalOfferContent } from '../external-offer-content.types';
import { CouldBeUnavailableNearby } from './components';
import { SurgeonWarning } from '../surgeon-warning/surgeon-warning.component';
import { ExternalOfferTitle } from './components/external-offer-title.component';

type ExternalOfferCardActions = Partial<
  Record<
    BannerActionType,
    {
      onAction: () => Promise<unknown>;
      inProgress: boolean;
    }
  >
>;

export type ExternalOfferCardProps = {
  onClick?: () => void;
  actions?: ExternalOfferCardActions;
  chips?: React.ReactNode[];
  content: ExternalOfferContent;
  shouldShowUnavailableWarning?: boolean;
};

type AccessoryTypeProps = {
  isLoading: boolean;
  onClick?: ExternalOfferCardProps['onClick'];
  actions?: ExternalOfferCardActions;
};

const getAccessoryType = ({ isLoading, onClick, actions }: AccessoryTypeProps) => {
  if (isLoading) {
    return 'spinner';
  }
  if (onClick && actions?.ACTIVATE) {
    return 'plusButtonClickable';
  }
  if (actions?.ACTIVATE) {
    return 'plusButton';
  }
  return 'none';
};

export const ExternalOfferCard = React.forwardRef<HTMLDivElement, ExternalOfferCardProps>(
  (props: ExternalOfferCardProps, ref) => {
    const {
      content: { title, titleMiddle, title2, imageUrl, surgeonGeneralWarning },
      shouldShowUnavailableWarning,
      onClick,
      actions,
      chips,
    } = props;

    const isLoading = useMemo(() => {
      return Object.values(actions || {}).some((v) => v?.inProgress);
    }, [actions]);

    const accessoryType = getAccessoryType({ isLoading, onClick, actions });

    return (
      <DynamicCard ref={ref} loading={isLoading} disabled={false} onClick={onClick}>
        {surgeonGeneralWarning && (
          <Box
            sx={(theme) => ({
              m: theme.spacing(SizingMultiplier.md, SizingMultiplier.md, -SizingMultiplier.sm),
            })}>
            <SurgeonWarning message={surgeonGeneralWarning} />
          </Box>
        )}
        <Box position="relative">
          <DynamicCardContent>
            {imageUrl && <DynamicCardMedia src={imageUrl} alt={title} objectFit="contain" />}

            <Stack
              direction="column"
              spacing={SizingMultiplier.sm}
              flex={1}
              alignItems="flex-start"
              justifyContent="center">
              <ExternalOfferTitle title={title} titleMiddle={titleMiddle} title2={title2} />
              {chips}
              {shouldShowUnavailableWarning && <CouldBeUnavailableNearby />}
            </Stack>
            <DynamicCardAccessory type={accessoryType} onClick={actions?.ACTIVATE?.onAction} />
          </DynamicCardContent>
        </Box>
      </DynamicCard>
    );
  },
);
